import Vue from "vue";
import Router from "vue-router";
import Home from "@/applications/skolebordetv2/views/HomeView.vue";
import axios from "axios";
import { JwtHelper } from "@/helpers/utilities/JwtHelper";
import { SessionHelper } from "@/helpers/utilities/sessionHelper";
import { useFeaturesStore } from "@/stores/features";
import { useSettingsStore } from "@/stores/settings";

Vue.use(Router);

let i18n;
let timeoutObj;

export const router = new Router({
  mode: "history", // Required for Adal library
  routes: [
    // Tile menus
    {
      path: "/",
      name: "home",
      component: Home,
      props: true
    },
    {
      path: "/tiles/:groupId",
      name: "tiles",
      component: Home,
      props: true
    },

    // Sections
    {
      path: "/sections",
      name: "sections",
      component: () => import("@/views/Section/SectionsView.vue")
    },
    {
      path: "/sections/:sectionId",
      name: "section",
      component: () => import("@/views/Section/SectionView.vue"),
      props: true
    },
    {
      path: "/sections/:sectionId/links",
      name: "sectionlinks",
      component: () => import("@/views/Section/SectionLinksView.vue"),
      props: true
    },

    // Confidentials v3
    // Confidentials v3
    {
      path: "/sections/:sectionId/conf/children",
      name: "confidentialChildrenView",
      component: () => import("@/views/Documents/V2/ChildrenView.vue"),
      props: true,
      beforeEnter: twoFactorGuard
    },
    {
      path: "/sections/:sectionId/conf/children/:childId/:groupId?/:itemId?",
      name: "confidentialsChildView",
      component: () => import("@/views/Documents/V2/ChildView.vue"),
      props: true,
      beforeEnter: twoFactorGuard
    },

    {
      path: "/sections/:sectionId/conf/:groupId?/:itemId?",
      name: "confidentialsSectionView",
      component: () => import("@/views/Documents/V2/SectionView.vue"),
      props: true,
      beforeEnter: twoFactorGuard
    },

    {
      path: "/combidrive/:systemId/:entityId/:groupId/:itemId?",
      name: "combidrive",
      component: () => import("@/views/Documents/V2/CombiDriveView.vue"),
      meta: {
        title: "Documents"
      },
      props: true
    },

    {
      path: "/people/unregistered",
      name: "confidentialChildrenUnregisteredView",
      component: () => import("@/views/people/confidential/PeopleUnregisteredView.vue"),
      props: true,
      meta: { roles: ["isSuperUser"], redirects: { isStudent: "home", isTeacher: "home" } },
      beforeEnter: twoFactorGuard
    },

    {
      path: "/people/unregistered/:childId/:groupId?/:itemId?",
      name: "confidentialsChildUnregisteredView",
      component: () => import("@/views/Documents/V2/ChildView.vue"),
      props: true,
      meta: { roles: ["isSuperUser"], redirects: { isStudent: "home", isTeacher: "home" } },
      beforeEnter: twoFactorGuard
    },
    {
      path: "/people/access",
      name: "confidentialPeopleView",
      component: () => import("@/views/people/confidential/PeopleAccessView.vue"),
      props: true,
      meta: { roles: ["isSuperUser"], redirects: { isStudent: "home", isTeacher: "home" } },
      beforeEnter: twoFactorGuard
    },

    {
      path: "/people/access/:personId",
      name: "confidentialPeopleAccessView",
      component: () => import("@/views/people/confidential/PersonAccessView.vue"),
      props: true,
      meta: { roles: ["isSuperUser"], redirects: { isStudent: "home", isTeacher: "home" } },
      beforeEnter: twoFactorGuard
    },

    // Documents
    {
      path: "/documents/:driveId?/:itemId?",
      name: "documents",
      component: () => import("@/views/Documents/DocumentsView.vue"),
      meta: {
        title: "Documents"
      },
      props: true
    },

    {
      path: "/mydocuments/:groupId?/:itemId?/:driveId?",
      name: "mydocuments",
      component: () => import("@/views/Documents/MyDocumentsView.vue"),
      meta: {
        title: "MyDocuments"
      },
      props: true
    },

    {
      path: "/*://*", // catch all routes with a protocol (external links)
      name: "external",
      beforeEnter: to => (window.location.href = to.path.substr(1))
    },
    {
      path: "*",
      name: "not-found",
      component: () => import("@/views/NotFound.vue")
    },

    // Links
    {
      path: "/links",
      name: "links",
      component: () => import("@/views/Links/LinksView.vue")
    },

    // Integrations
    {
      path: "/os2faktorlogin/:to?/:from?",
      name: "os2FaktorLogin",
      component: () => import("@/views/Integrations/os2FaktorLoginView"),
      props: true
    }
  ]
});

router.beforeEach((to, from, next) => {
  // Check for session token
  const sessionToken = SessionHelper.nctSessionToken();

  if (!sessionToken) {
    return;
  }

  if (to.meta.roles) {
    // Check for authorization for route
    const allowedRoles = to.meta.roles.find(r => router.app.$roles[r]);

    if (!allowedRoles) {
      if (router.app.$roles.isSuperUser) {
        next({ name: to.meta.redirects.isSuperUser, params: to.params });
      }

      if (router.app.$roles.isTeacher) {
        next({ name: to.meta.redirects.isTeacher, params: to.params });
      }

      if (router.app.$roles.isStudent) {
        next({ name: to.meta.redirects.isStudent, params: to.params });
      }
    }
  }

  // Lookup default title from store
  const browserTitle = document.title.split(" - ")[0];

  if (browserTitle) {
    // Find nearest route up the tree with a title
    const nearestWithTitle = to.matched
      .slice()
      .reverse()
      .find(r => r.meta && r.meta.title);

    // If a route with a title was found, set the document (page) title to that value.
    document.title =
      browserTitle + (nearestWithTitle && i18n ? " - " + i18n.t("Routes." + nearestWithTitle.meta.title) : "");
  }

  // Send simple stats
  if (import.meta.env.PROD) {
    const activeInstitutionId = JwtHelper.getInstitutionId(SessionHelper.nctSessionToken());

    axios.post(
      "https://skobostats.azurewebsites.net/api/NavigationEvent",
      {
        UserId: router.app?.$skoboUser?.userId,
        InstitutionId: activeInstitutionId,
        ToRoute: to.path,
        FromRoute: from.path,
        Role: router.app?.$roles?.isTeacher ? "teacher" : router.app?.$roles?.isSuperUser ? "superuser" : "student",
        UserAgent: navigator.userAgent,
        Language: navigator.language
      },
      { headers: { "x-functions-key": "eslMWIyl83a57fwj2ge3aG6LPqQyVKeTwH0ulpf4XSrDQVpeqwC4Ow==" } }
    );
  }

  next();
});

router.afterEach(() => {
  clearTimeout(timeoutObj);

  timeoutObj = setTimeout(() => {
    SessionHelper.logout();
  }, 3600000);
});

/**
 * Set translation
 * @param { object } i i18n translation object
 */
export function setTranslater(i) {
  i18n = i;
}

/**
 * Check for two factor authentication
 * @param { object } to To route
 * @param { object } from From route
 * @param { Function } next Next function
 * @returns { Function } Next route
 */
async function twoFactorGuard(to, from, next) {
  const featuresStore = useFeaturesStore();
  const settingsStore = useSettingsStore();

  if (featuresStore.isFeatureEnabled("OS2Faktor")) {
    return await import("@/helpers/integrations/os2FaktorHelper").then(os2FaktorHelper => {
      if (os2FaktorHelper.isSecondAuthentication(settingsStore.setting("os2faktorSessionTimeout"))) {
        return next();
      }
      router.push({ name: "os2FaktorLogin", params: { to: to.path, from: from.path } });
      return false;
    });
  } else {
    return next();
  }
}
